import { Navigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { api } from '@/services/api'

type ProtectedRouteProps = {
  children: React.ReactNode
  skipOnboardingCheck?: boolean
}

const ONBOARDING_PATH = '/onboarding'

function isOnboardingEnabled(): boolean {
  const flag = import.meta.env.VITE_ONBOARDING_WIZARD_ENABLED as
    | string
    | undefined
  return flag !== 'false'
}

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
    </div>
  )
}

export function ProtectedRoute({
  children,
  skipOnboardingCheck = false,
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  const { data: profile, isLoading: profileLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => api.getUserProfile(),
    enabled: isAuthenticated,
    retry: false,
  })

  if (isLoading) return <LoadingSpinner />
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  if (profileLoading) return <LoadingSpinner />

  const needsOnboarding =
    !skipOnboardingCheck
    && isOnboardingEnabled()
    && profile
    && !profile.onboardingCompletedAt
    && location.pathname !== ONBOARDING_PATH

  if (needsOnboarding) {
    return <Navigate to={ONBOARDING_PATH} replace />
  }

  return <>{children}</>
}
